export const AI_NAVIGATION_REGISTRY = {
  login: { label: "ログイン", path: "/login" },
  ai_inquiry_list: { label: "AI問い合わせ", path: "/list_mess" },
  worker_list: { label: "社員一覧", path: "/workers" },
  worker_create: { label: "社員の新規登録", path: "/workers/create" },
  permission_management: {
    label: "権限管理",
    path: "/permissions_management",
  },
  role_permissions: { label: "ロール権限", path: "/role_permissions" },
  salary_overview: {
    label: "給与明細・支払計算書",
    path: "/annual_salary_summaries",
  },
} as const;

export type AiNavigationTarget = "none" | keyof typeof AI_NAVIGATION_REGISTRY;

export type AiRenderStep = {
  navigation_target: AiNavigationTarget;
  text: string;
};

export type AiRenderPayload = {
  version: 1;
  summary: string;
  steps: AiRenderStep[];
  notes: string[];
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const isAiNavigationTarget = (
  value: unknown,
): value is AiNavigationTarget => {
  if (value === "none") return true;

  return (
    typeof value === "string" &&
    Object.prototype.hasOwnProperty.call(AI_NAVIGATION_REGISTRY, value)
  );
};

/**
 * Validate the server payload before rendering any model-controlled values.
 * Invalid payloads deliberately return null so callers can use content as
 * plain text without exposing an unknown target or route.
 */
export const parseAiRenderPayload = (
  value: unknown,
): AiRenderPayload | null => {
  if (!isRecord(value)) return null;
  if (value.version !== 1 || typeof value.summary !== "string") return null;
  if (
    !Array.isArray(value.steps) ||
    value.steps.length === 0 ||
    !Array.isArray(value.notes)
  ) {
    return null;
  }

  const steps: AiRenderStep[] = [];
  for (const step of value.steps) {
    if (!isRecord(step)) return null;
    if (
      typeof step.text !== "string" ||
      !isAiNavigationTarget(step.navigation_target)
    ) {
      return null;
    }

    steps.push({
      navigation_target: step.navigation_target,
      text: step.text,
    });
  }

  if (!value.notes.every((note) => typeof note === "string")) return null;

  return {
    version: 1,
    summary: value.summary,
    steps,
    notes: value.notes,
  };
};
